import { Suspense, useMemo, useState } from 'react';

import { iProps } from './SmartSendFilterBar';
import { ReadyNotReady } from './ReadyNotReady';
import {
  P2GBox,
  P2GChip,
  P2GFade,
  P2GGrid,
  P2GLink,
  P2GSticky,
  P2GTooltip,
  useP2GMediaQueryDown
} from '@parcel2go/core-ui';
import { ToInlineCommaList } from '../../../lib/helpers/StringHelper';
import DraggableSort from './DraggableSort';
import DebouncedInput from '../../DebouncedInput';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import P2GContentDropDown from '../../P2GContentDropDown';
import QuickSelect from '../quickselect/QuickSelect';

export function SmartSendFilterBarDesktop(props: iProps) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const isSmall = useP2GMediaQueryDown('lg');
  const [open, setOpen] = useState(false);

  const states = useMemo(() => {
    const statuses = props.summary.states
      .filter((x) => ['QUOTING', 'SAVE'].includes(x))
      .map((x) => {
        switch (x) {
          case 'QUOTING':
            return 'Quoting';
          case 'SAVE':
            return 'Saving';
        }
      });
    return statuses.length > 0 && ToInlineCommaList(statuses);
  }, [props.summary.states]);

  return (
    <P2GSticky align="top" backgroundColor="transparent">
      <P2GBox pb={2} sx={(t) => ({ backgroundColor: t.palette.grey.A100 })}>
        <P2GGrid container gap={2} alignItems="center" wrap="nowrap">
          {props.sold && (
            <P2GGrid item flexShrink={1} classNames="p2g-neworders-tabs">
              <P2GBox sx={{ minWidth: 250 }}>
                <ReadyNotReady {...props} showDropdown={isSmall} />
              </P2GBox>
            </P2GGrid>
          )}

          <P2GGrid item flexGrow={1}>
            <P2GFade in={!!states}>
              <P2GChip label={states} size="small" />
            </P2GFade>
          </P2GGrid>

          <P2GGrid item>
            <P2GBox sx={{ minWidth: 300 }}>
              <DebouncedInput
                id="Search"
                label={t('SEARCH_INPUT_LABEL', { defaultValue: 'Find by order details...' })}
                value={props.filter.SearchTerm}
                onChange={(v) => props.update({ ...props.filter, SearchTerm: v })}
                disabled={props.filter.DisableFilter}
              />
            </P2GBox>
          </P2GGrid>

          {!props.filter.DisableFilter && (
            <P2GGrid item flexShrink={1}>
              <P2GTooltip title={t('FILTER_ORDERS_TOOLTIP_TITLE', { defaultValue: 'Filter Orders' })} arrow>
                <P2GLink onClick={props.openFilter}>
                  {props.activeFilters > 2 ? (
                    <strong>{t('FILTER_ORDERS_TOOLTIP_BUTTON', { defaultValue: 'Filter' })}</strong>
                  ) : (
                    t('FILTER_ORDERS_TOOLTIP_BUTTON', { defaultValue: 'Filter' })
                  )}
                </P2GLink>
              </P2GTooltip>
            </P2GGrid>
          )}

          <P2GGrid item flexShrink={1}>
            <DraggableSort
              {...props}
              control={
                <P2GTooltip title={t('SORT_ORDERS_TOOLTIP_TITLE', { defaultValue: 'Sort and arrange orders' })} arrow>
                  <P2GLink>{t('SORT_BY_TOOLTIP_BUTTON', { defaultValue: 'Sort' })}</P2GLink>
                </P2GTooltip>
              }
            />
          </P2GGrid>

          <P2GGrid item>
            <P2GContentDropDown
              onOpen={() => setOpen(true)}
              children={
                <P2GBox mb={2}>
                  {open && (
                    <Suspense>
                      <QuickSelect
                        filterKey={props.filterKey}
                        restrictToSold={true}
                        selected={props.summary.selected.length}
                        isXs={false}
                      />
                    </Suspense>
                  )}
                </P2GBox>
              }
              control={<P2GLink>{t('SELECT_BUTTON', { defaultValue: 'Select' })}</P2GLink>}
            />
          </P2GGrid>
        </P2GGrid>
      </P2GBox>
    </P2GSticky>
  );
}
